import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useModelChoice } from '../hooks/useModelChoice';
import { cn } from '../lib/cn';
import { type Effort, MODEL_CATALOG, type Model, effortOptionsForModel } from '../lib/settings';
import { Sheet, SheetRow } from './Sheet';

const MODEL_HINTS: Record<string, string> = {
  maclaren: 'Агент Notion: веб-поиск, файлы и терминал',
};

/**
 * Header title that names the current model and opens the model sheet,
 * like the model menu in native chat apps. The choice applies to the
 * next message.
 */
export function ModelSwitcher({ subtitle }: { subtitle?: string }) {
  const { model, effort, allowed, setModel, setEffort } = useModelChoice();
  const [open, setOpen] = useState(false);
  const models = MODEL_CATALOG.filter((m) => allowed == null || allowed.includes(m.id));
  const current = MODEL_CATALOG.find((m) => m.id === model);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={models.length === 0}
        aria-haspopup="dialog"
        className="mc-press flex min-h-11 min-w-0 max-w-full flex-col items-center justify-center rounded-xl px-3 active:bg-subtle md:hover:bg-subtle"
      >
        {subtitle && (
          <span className="block max-w-full truncate text-[15px] font-semibold leading-tight">
            {subtitle}
          </span>
        )}
        <span
          className={cn(
            'inline-flex max-w-full items-center gap-1 leading-tight',
            subtitle
              ? 'text-[12.5px] text-muted-foreground'
              : 'text-[16px] font-semibold tracking-[-0.01em]',
          )}
        >
          <span className="truncate">{current?.label ?? 'Нет доступных моделей'}</span>
          <ChevronDown
            size={subtitle ? 13 : 16}
            className="shrink-0 opacity-60"
            aria-hidden="true"
          />
        </span>
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} title="Модель">
        <ModelOptions
          value={model}
          effort={effort}
          allowed={allowed}
          onChange={setModel}
          onEffortChange={setEffort}
        />
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="mc-press mt-5 min-h-12 w-full rounded-2xl bg-foreground text-[16px] font-medium text-background"
        >
          Готово
        </button>
      </Sheet>
    </>
  );
}

/** Model list + reasoning switch shown in the sheet. Pure, so it can be
 * rendered from any catalog entry in tests. */
export function ModelOptions({
  value,
  effort,
  allowed,
  onChange,
  onEffortChange,
}: {
  value: Model;
  effort: Effort;
  allowed?: string[] | null;
  onChange: (model: Model) => void;
  onEffortChange: (effort: Effort) => void;
}) {
  const models = MODEL_CATALOG.filter((m) => allowed == null || allowed.includes(m.id));
  const efforts = effortOptionsForModel(value);
  return (
    <>
      <div role="menu" aria-label="Модель" className="space-y-1">
        {models.map((m) => (
          <SheetRow
            key={m.id}
            role="menuitemradio"
            value={m.id}
            label={m.label}
            hint={MODEL_HINTS[m.id]}
            selected={m.id === value}
            onClick={() => onChange(m.id)}
          />
        ))}
      </div>
      {efforts.length > 0 && (
        <div className="mt-4 px-2">
          <div className="mb-2 text-[13px] font-medium text-muted-foreground">Мышление</div>
          <div
            role="radiogroup"
            aria-label="Мышление"
            className="grid rounded-2xl bg-subtle p-1"
            style={{ gridTemplateColumns: `repeat(${efforts.length}, minmax(0, 1fr))` }}
          >
            {efforts.map((option) => {
              const active = option.id === effort;
              return (
                <button
                  key={option.id}
                  type="button"
                  // biome-ignore lint/a11y/useSemanticElements: segmented control styled like iOS
                  role="radio"
                  aria-checked={active}
                  data-value={option.id}
                  onClick={() => onEffortChange(option.id)}
                  className={cn(
                    'min-h-11 rounded-xl text-[15px] transition-[background-color,box-shadow,color] duration-200',
                    active
                      ? 'bg-surface font-medium text-foreground shadow-sm'
                      : 'text-muted-foreground',
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
